/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import {
  ChatMessage as CdsLabsChatMessage,
  type ChatMessagesContainerRef,
  type CollapseState,
} from '@coursera-web/cds-labs';
import classNames from 'classnames';

import { get as getUser } from 'js/lib/user';

import { breakpoints } from '@coursera/cds-core';

import {
  type ProgressiveLoaderProps,
  getLoadingMessagesForAction,
} from 'bundles/ai-coach-platform/components/building-blocks';
import ChatFooter from 'bundles/ai-coach-platform/components/patterns/chat/ChatFooter';
import type { FeedbackIssueOption } from 'bundles/ai-coach-platform/components/patterns/chat/FeedbackModal';
import {
  ChatMessage,
  ChatMessageBody,
  type ChatMessageBodyProps,
  ChatMessageContainer,
  type ChatMessageProps,
} from 'bundles/ai-coach-platform/components/patterns/chat/common';
import ChatEmptyState from 'bundles/ai-coach-platform/components/patterns/chat/common/ChatEmptyState';
import { MESSAGE_SENDER } from 'bundles/ai-coach-platform/components/patterns/chat/constants';
import { useCoachChatContext } from 'bundles/ai-coach-platform/components/patterns/chat/context/CoachChatContext';
import type { CoachPersona, SendMessageFunc } from 'bundles/ai-coach-platform/components/patterns/chat/types';
import {
  getCoachPersona,
  useLearnerPersona,
} from 'bundles/ai-coach-platform/components/patterns/chat/utils/personaUtils';

import _t from 'i18n!nls/ai-coach-platform';

import type { GlobalState, MessageTypes } from './store/types';
import { isClientMessage as isClientMessageUtil, isCoachMessage as isCoachMessageUtil } from './utils/messageUtils';

export type HideMessageType = ({
  message,
  index,
  messages,
}: {
  message: MessageTypes;
  index: number;
  messages: Array<MessageTypes>;
}) => boolean;

export type Props = {
  sendMessage: SendMessageFunc;
  emptyState?: React.ReactElement;

  renderCustomMessage?: (props: ChatMessageProps) => React.ReactElement | null;
  renderCustomMessageLoader?: (props: ProgressiveLoaderProps) => React.ReactElement | null;
  renderCustomTemporaryStreamedMessage?: (props: ChatMessageBodyProps) => React.ReactElement | null;
  renderTemporaryMessage?: () => React.ReactElement | null;

  /**
   * Hides the message in the chat. This function accepts an individual message and its index
   * returns a boolean value if the message needs to be hidden based on the function's implementation
   * at the parent level.
   * Eg: Hiding the first "initiation" message and chat summary message for coach item use case.
   */
  hideMessage?: HideMessageType;
  /** Hides all message actions (copy + feedback) on every rendered message, e.g. dialogue intro messages. */
  hideMessageActions?: boolean;
  /** Hides only the copy button (keeps feedback) on every rendered message, e.g. graded role plays. */
  hideCopyButton?: boolean;
  activeChatSessionId?: string;
  coachPersona?: CoachPersona;
  customFooter?: React.ReactElement | null;
  forceHideMessageLoader?: boolean;
  enableTextStreaming?: boolean;
  enableContinuousChat?: boolean;
  /**
   * Opts into collapsible-surface behaviour, for a caller that renders the chat as a tray/preview
   * that can toggle between a short peek and a full-height panel. Leave undefined for an ordinary
   * chat.
   *
   * `'collapsed'`: disables scrolling (pins to the latest message), skips the last message's
   * min-height reservation, and hides the short container legal disclaimer since the regular
   * disclaimer is rendered instead.
   *
   * `'expanded'`: reserves exactly one scrollport under the last message so it can still
   * top-anchor while streaming.
   */
  collapseState?: CollapseState;
  setOldestMessageRef?: (node: HTMLDivElement | null) => void;
  'aria-label'?: string;
  recommendedActionsLabel?: string;
  feedbackIssueOptions?: FeedbackIssueOption[];
  onFeedbackSubmitted?: (args: { messageId: string; userIssue?: string; userComments: string }) => void;
  /** Scoped scroll handle from ChatMessagesContainer. Native scrollIntoView bubbles to
   *  every scrollable ancestor, which shifts the page header when the chat is embedded. */
  scrollContainerRef?: React.RefObject<ChatMessagesContainerRef | null>;
};

// exported for direct unit testing, since asserting emotion-generated CSS via the DOM is fragile
export function getLastMessageStyles(
  enableTextStreaming = false,
  enableContinuousChat = false,
  collapseState?: CollapseState
) {
  if (!enableTextStreaming || collapseState === 'collapsed') return null;

  // An expanded collapsible surface is only as tall as its own scrollport, so the viewport-based
  // reservation below would leave several screens of blank space under the last message. One
  // scrollport is exactly enough for the streaming top-anchor to reach -- `cqh` resolves against
  // the containment context ChatMessagesContainer's `containSize` establishes, so no breakpoints.
  if (collapseState === 'expanded') {
    return css`
      min-height: 100cqh;

      ${enableContinuousChat && 'padding-bottom: var(--cds-spacing-50);'}
    `;
  }

  // Reserve enough min-height so the last message can be scrolled to the top of the
  // container. The offset approximates header + composer + padding for the scoped
  // CDS Labs scroll container.
  const offset = 300;
  const offsetMd = 240;
  const offsetXs = 200;
  return css`
    min-height: calc(100vh - ${offset}px);

    ${enableContinuousChat && 'padding-bottom: var(--cds-spacing-50);'}

    ${breakpoints.down('md')} {
      min-height: calc(100vh - ${offsetMd}px);
    }

    ${breakpoints.down('xs')} {
      min-height: calc(100vh - ${offsetXs}px);
    }
  `;
}

const styles = {
  messagesContainer: css`
    padding: 0 var(--cds-spacing-200);

    /* Hide scrollbar for Chrome, Safari and Opera */
    ::-webkit-scrollbar {
      display: none;
    }

    height: 100%;
    max-height: none;

    /* Hide scrollbar for IE, Edge and Firefox */
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */

    ${breakpoints.down('xs')} {
      height: 100%;
      max-height: none;
    }
  `,
  message: css`
    margin: var(--cds-spacing-200) 0;
    word-wrap: break-word;

    svg {
      flex-shrink: 0;
    }

    scroll-margin-top: var(--cds-spacing-200);

    &:focus {
      outline: none;
    }

    .coach-chat-message-body {
      /* aligns body with start of sender name */
      margin-inline-start: 40px;
      margin-top: calc(-1 * var(--cds-spacing-100));
    }
  `,
  streamedMessage: css`
    scroll-margin-top: var(--cds-spacing-200);
  `,
  smallScreenDisclaimer: css`
    display: none;

    @container coach-chat (max-height: 480px) {
      display: block;
    }
  `,
  lastMessage: getLastMessageStyles,
};

type LoadingStateProps = {
  coachPersona: CoachPersona;
  action: string | undefined;
};

const renderLoadingState = ({ action }: LoadingStateProps) => {
  return <CdsLabsChatMessage variant="llm" isLatestMessage loadingMessages={getLoadingMessagesForAction(action)} />;
};

type StreamedMessageProps = {
  temporaryStreamedMessageProps: ChatMessageBodyProps;
};

const renderStreamedMessage = ({ temporaryStreamedMessageProps }: StreamedMessageProps) => {
  return (
    <CdsLabsChatMessage
      variant="llm"
      isLatestMessage
      content={<ChatMessageBody {...temporaryStreamedMessageProps} />}
    />
  );
};

const ChatMessages = ({
  emptyState,
  hideMessage,
  hideMessageActions,
  hideCopyButton,
  sendMessage,
  activeChatSessionId: chatSessionId,
  renderCustomMessage = () => null,
  renderCustomMessageLoader = () => null,
  renderTemporaryMessage = () => null,
  renderCustomTemporaryStreamedMessage = () => null,
  coachPersona = getCoachPersona(),
  customFooter,
  forceHideMessageLoader,
  enableTextStreaming = false,
  enableContinuousChat,
  collapseState,
  setOldestMessageRef,
  'aria-label': ariaLabel,
  recommendedActionsLabel,
  feedbackIssueOptions,
  onFeedbackSubmitted,
  scrollContainerRef,
}: Props) => {
  const { disableProgrammaticScroll, chatPrefixContent } = useCoachChatContext();
  const isCollapsed = collapseState === 'collapsed';
  const { messages, showMessageLoader, activeChatSessionId, isMessageStreaming, streamedMessage, embeddedContents } =
    useSelector((state: GlobalState) => ({
      messages: (chatSessionId && state.messages.messagesBySessionId[chatSessionId]) || state.messages.messages,
      showMessageLoader: state.behavior.messageLoader,
      activeChatSessionId: state.sessionState?.chatSessionId,
      isMessageStreaming: state.behavior.isMessageStreaming,
      streamedMessage: state.messages.streamedMessage,
      embeddedContents: state.messages.embeddedContents,
    }));
  const { isSearchMode, searchTerm } = useCoachChatContext();

  // ref for the last message in the chat, either final message or temporary streamed message
  const lastMessageRef = useRef<HTMLDivElement | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);

  // keep track of previous isMessageStreaming value
  const previousIsMessageStreaming = useRef(isMessageStreaming);
  // track if this is the initial load
  const isInitialLoad = useRef(true);
  // set to true when a recommended action button is clicked, so the next coach response receives focus
  const shouldFocusLastMessage = useRef(false);

  const handleRecommendedActionClick = React.useCallback<() => void>(() => {
    shouldFocusLastMessage.current = true;
  }, []);

  const messageCount = messages.length;
  const { id: userId } = getUser();

  const learnerPersona = useLearnerPersona();

  const showCoachLoader =
    !forceHideMessageLoader && showMessageLoader && (activeChatSessionId === chatSessionId || !chatSessionId);

  // scroll latest message into view every time `messageCount` updates i.e. new message is added
  // or after coach loader gets hidden (for streamed messages)
  useEffect(() => {
    const isStreamingComplete = enableTextStreaming
      ? previousIsMessageStreaming.current === true && isMessageStreaming === false
      : false;

    const skipScroll = isStreamingComplete;

    if (chatPrefixContent && messageCount <= 2) {
      // skip scroll when showing the prefix content, since we want to ensure this content to be visible
      // to the user and scrolling to the latest message might move this above the viewport.
      return;
    }

    // we already scroll to the latest streaming message so we want to skip this again in the case
    // where the user has manually scrolled down we shouldn't auto-scroll them back up to the top of the message.
    // and when a user requests to fetch earlier messages, we disable this programmatic scroll behavior so they can can see the earlier messages at the top of the chat
    if (!skipScroll && !disableProgrammaticScroll && lastMessageRef?.current) {
      scrollContainerRef?.current?.scrollElementIntoView(lastMessageRef.current, { behavior: 'smooth' });
    }

    // a11y: when a recommended action button is clicked, it disappears from the DOM so focus cannot
    // remain on it. move focus to the new coach response instead — after streaming completes for
    // streaming responses, or once the loader is gone for non-streaming responses.
    if (shouldFocusLastMessage.current && !disableProgrammaticScroll) {
      const canFocus = enableTextStreaming ? isStreamingComplete : !showCoachLoader && !isMessageStreaming;
      if (canFocus) {
        lastMessageRef?.current?.focus();
        shouldFocusLastMessage.current = false;
      }
    }

    previousIsMessageStreaming.current = isMessageStreaming;
  }, [
    messageCount,
    showCoachLoader,
    isMessageStreaming,
    enableTextStreaming,
    disableProgrammaticScroll,
    chatPrefixContent,
    scrollContainerRef,
  ]);

  // Helper function to determine which ref to use
  const getMessageRef = (isLast: boolean, isFirst: boolean) => {
    if (isLast && !isMessageStreaming) return lastMessageRef;
    if (isFirst && setOldestMessageRef) return setOldestMessageRef;
    return null;
  };

  // scroll to bottom of last message on initial load
  useEffect(() => {
    if (isInitialLoad.current && messageCount && enableContinuousChat) {
      isInitialLoad.current = false;
      // a11y (MERCH-3101): skip the load-time scroll-to-latest entirely when the consumer opts out,
      // so it can't drag focus/VoiceOver into the chat on load.
      if (!disableProgrammaticScroll && lastMessageRef?.current) {
        scrollContainerRef?.current?.scrollElementIntoView(lastMessageRef.current, {
          behavior: 'instant',
          block: 'end',
        });
      }
    }
  }, [messageCount, enableContinuousChat, disableProgrammaticScroll, scrollContainerRef]);

  const emptyStateRenderer = () => {
    return emptyState ?? <ChatEmptyState />;
  };

  const isEmptyState = !messages?.length;

  if (isEmptyState && !chatPrefixContent) {
    return (
      <>
        {emptyStateRenderer()}
        {/* Conditionally show the disclaimer below the empty state on smaller screens so that it remains
            accessible to learners in reflow and resize text accessibility cases (WCAG 1.4.4, 1.4.10) */}
        {!isCollapsed && (
          <div css={styles.smallScreenDisclaimer} data-testid="small-screen-disclaimer">
            {customFooter || <ChatFooter />}
          </div>
        )}
      </>
    );
  }

  const temporaryStreamedMessageProps: ChatMessageBodyProps = {
    message: {
      text: streamedMessage || '',
      type: 'text',
      sender: MESSAGE_SENDER.RESPONSE,
      showAvatar: true,
      timestamp: Date.now(),
      unread: false,
      embeddedContents: embeddedContents ?? undefined,
    },
    isStreaming: true,
  };

  return (
    // a11y implementation: https://www.w3.org/WAI/WCAG21/working-examples/aria-role-log/chatlog.html
    // see COACH-1236
    <div role="log">
      {chatPrefixContent && <div>{chatPrefixContent}</div>}
      <div
        data-testid="ai-coach-chat-messages"
        ref={messagesContainerRef}
        css={styles.messagesContainer}
        aria-label={ariaLabel ?? _t('Coursera AI Chat messages')}
        role="list"
      >
        {messages?.map((message, index) => {
          const { timestamp, sender } = message;

          const isLastMessage = index === messages.length - 1;
          const isFirstMessage = index === 0;

          const isCoachMessage = isCoachMessageUtil(sender);
          const isClientMessage = isClientMessageUtil(sender);

          if (hideMessage && hideMessage({ message, index, messages })) {
            return null;
          }

          const messageProps: ChatMessageProps = {
            message,
            index,
            messages,
            sendMessage,
            learnerPersona,
            coachPersona,
            onRecommendedActionClick: handleRecommendedActionClick,
            feedbackIssueOptions,
            onFeedbackSubmitted,
          };

          return (
            <ChatMessageContainer
              key={isCoachMessage ? message.customId : `${userId}-${timestamp.valueOf()}-${index + 1}`}
              className={classNames({
                'coach-message-client': isClientMessage,
                'coach-message-response': isCoachMessage,
              })}
              ref={getMessageRef(isLastMessage, isFirstMessage)}
              css={[
                styles.message,
                isLastMessage &&
                  isCoachMessage &&
                  styles.lastMessage(enableTextStreaming, enableContinuousChat, collapseState),
              ]}
              role="listitem"
              tabIndex={isLastMessage && isCoachMessage ? -1 : undefined}
            >
              {renderCustomMessage(messageProps) ?? (
                // hide recommended actions in search mode to prevent sending new messages in this mode
                <ChatMessage
                  {...messageProps}
                  hideRecommendedActions={enableContinuousChat && isSearchMode()}
                  hideMessageActions={hideMessageActions}
                  hideCopyButton={hideCopyButton}
                  searchTerm={searchTerm}
                  recommendedActionsLabel={recommendedActionsLabel}
                />
              )}
            </ChatMessageContainer>
          );
        })}

        {renderTemporaryMessage()}

        {/* show loading message when streaming is either disabled or not yet started */}
        {showCoachLoader && !isMessageStreaming && (
          <ChatMessageContainer
            role="status"
            aria-live="polite"
            css={[styles.message, styles.lastMessage(enableTextStreaming, false, collapseState)]}
            className="coach-message-loader"
          >
            {renderCustomMessageLoader({ action: messages[messages.length - 1]?.action }) ??
              renderLoadingState({
                coachPersona,
                action: messages[messages.length - 1]?.action,
              })}
          </ChatMessageContainer>
        )}

        {/* show temporary streamed message when available */}
        {streamedMessage && (
          <ChatMessageContainer
            className="coach-message-streamed"
            ref={lastMessageRef}
            css={[
              styles.message,
              styles.streamedMessage,
              styles.lastMessage(enableTextStreaming, enableContinuousChat, collapseState),
            ]}
            aria-hidden="true" // hide from screen readers since this is a temporary message
          >
            {renderCustomTemporaryStreamedMessage(temporaryStreamedMessageProps) ??
              renderStreamedMessage({
                temporaryStreamedMessageProps,
              })}
          </ChatMessageContainer>
        )}
        {/* Conditionally show the disclaimer below the last message on smaller screens so that it remains
            accessible to learners in reflow and resize text accessibility cases (WCAG 1.4.4, 1.4.10) */}
        {!isCollapsed && (
          <div css={styles.smallScreenDisclaimer} data-testid="small-screen-disclaimer">
            {customFooter || <ChatFooter />}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatMessages;
